import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community'
import type { ReactElement } from 'react'
import type { Menu } from '~/components/ActionMenu'
import type { IBadgeCellRendererParams } from '~/types/badge'
import type { IReportReviewMacro } from '~/types/review'

import ActionMenu from '~/components/ActionMenu'
import ApplicationsListCellRenderer from '~/components/CellRenderers/ApplicationsListCellRenderer'
import BadgeCellRenderer from '~/components/CellRenderers/BadgeCellRenderer'
import LongTextCellRenderer from '~/components/CellRenderers/LongTextCellRenderer'
import TextCellRenderer from '~/components/CellRenderers/TextCellRenderer'
import TooltipHeaderTemplate from '~/components/TooltipHeaderTemplate'
import { actionsColDef, applicationsListColDef, badgeColDef, longTextColDef, textColDef } from '~/utils/table'

export interface IContext {
  handleDelete: (isOpen: boolean, macro?: IReportReviewMacro) => () => void
  handleEdit: (isOpen: boolean, macro?: IReportReviewMacro) => () => void
  reviewsWriteAuthorized: boolean
}

const ActionsCellRenderer = ({
  context,
  data,
}: ICellRendererParams<IReportReviewMacro, unknown, IContext>): ReactElement => {
  const { handleDelete, handleEdit, reviewsWriteAuthorized } = context

  if (data) {
    const menu: Menu = {
      actions: [
        {
          action: handleEdit(true, data),
          label: 'Edit',
          type: 'default',
        },
        {
          action: handleDelete(true, data),
          label: 'Delete',
          type: 'default',
        },
      ],
    }

    return <ActionMenu disabled={!reviewsWriteAuthorized} menu={menu} placement="top" />
  }

  return <div />
}

const AutomationKeywordsTooltip = (): ReactElement => {
  return (
    <div className="automation-keywords-tooltip">
      <span>Reviews containing these keywords will be automatically reported according to the concern.</span>
    </div>
  )
}

const columns: ColDef<IReportReviewMacro>[] = [
  applicationsListColDef(ApplicationsListCellRenderer, {
    field: 'applications',
  }),
  longTextColDef(LongTextCellRenderer, {
    field: 'content',
    headerName: 'Macro',
  }),
  textColDef(TextCellRenderer, {
    field: 'reason',
    headerName: 'Concern',
    tooltipField: undefined,
  }),
  textColDef(TextCellRenderer, {
    field: 'subject',
    headerName: 'Subject',
    tooltipField: undefined,
  }),
  badgeColDef(BadgeCellRenderer, {
    field: 'keywords',
    filterValueGetter: ({ data }: ValueGetterParams<IReportReviewMacro>): string[] | undefined => data?.keywords,
    headerComponentParams: {
      template: TooltipHeaderTemplate({ title: 'Automation keywords' }),
    },
    headerName: '',
    headerTooltip: 'Automation keywords',
    minWidth: 230,
    tooltipComponent: AutomationKeywordsTooltip,
    valueGetter: ({ data }: ValueGetterParams<IReportReviewMacro>): IBadgeCellRendererParams | undefined => {
      if (!data) return undefined

      const { keywords } = data

      return {
        data:
          keywords?.map((name) => ({
            id: name,
            name,
          })) || [],
        type: 'keyword',
      }
    },
  }),
  actionsColDef(ActionsCellRenderer, {
    field: 'actions',
  }),
]

export default columns
